import type { ContactsBulkRequestBlock } from "@containers/Hermes/types";
import useContactsRequests from "@containers/Hermes/hooks/useContactsRequests";

/**
 * The standing line under a Fetch Contacts card once a >10-company batch has
 * been accepted. The contacts arrive later by email / in Request Center, so
 * this shows where the request currently stands — in progress, delivered (with
 * a fresh download link) or failed — and always links to Request Center.
 */

type ContactsBulkRequestProps = {
  block: ContactsBulkRequestBlock;
  conversationId: string;
  messageId: number;
};

const DEFAULT_TRACKING_MESSAGE = "You can track the status in Request Center";
const DEFAULT_REQUEST_CENTRE_PATH = "/dashboard/request-centre?country=multicountry&type=import";
const linkCls = "tw-text-primary-purple-600 tw-font-medium hover:tw-underline";

export default function ContactsBulkRequest({ block, conversationId, messageId }: ContactsBulkRequestProps) {
  const { data } = useContactsRequests(conversationId);

  // Matched on request_id (one id for the whole batch); message_id narrows it
  // in case the same id ever shows up on two messages.
  const status = data?.requests.find((entry) => String(entry.request_id) === String(block.request_id) && entry.message_id === messageId);

  const message = status?.message || block.tracking_message || DEFAULT_TRACKING_MESSAGE;
  const requestCentrePath = status?.request_centre_path || block.request_centre_path || DEFAULT_REQUEST_CENTRE_PATH;
  const failed = status?.state === "failed";
  const downloadUrl = status?.state === "delivered" ? status.download_url : null;

  return (
    <p role={failed ? "alert" : "status"} className={`tw-m-0 tw-mt-3 tw-text-xs ${failed ? "tw-text-red-600" : "tw-text-gray-600"}`}>
      {message}
      {downloadUrl ? (
        <>
          {" · "}
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className={linkCls}>
            Download contacts
          </a>
        </>
      ) : null}
      {" · "}
      <a href={requestCentrePath} target="_blank" rel="noopener noreferrer" className={linkCls}>
        Open Request Center
      </a>
    </p>
  );
}
